import React, {useEffect} from 'react'
import {useTranslation} from 'react-i18next'
import {useHistory, useParams} from 'react-router-dom'

import {Box, Typography} from '@mui/material'

import {ReactComponent as CheckGraphicSVG} from '@progyny/assets/icons/big-check.svg'
import SubmitButton from '@progyny/components/Buttons/SubmitButton'
import PageLoader from '@progyny/components/PageLoader'
import SimpleMessageContainer from '@progyny/components/SimpleMessageContainer'
import {
  MeDocument,
  useConfirmUserEmailMutation,
} from '@progyny/generated/graphql'
import {RouteEnum, useRoutes} from '@progyny/routes'

import {theme} from '@root/theme'

interface ValidateTokenProps {}

/**
 * Validate token here to confirm user's email address.
 * Once token is validated on BE, cookie is set on header, which allows user to navigate through private routes.
 */
const ValidateToken: React.ComponentType<ValidateTokenProps> = () => {
  const {t} = useTranslation()
  const {token} = useParams() as {token: string}
  const history = useHistory()

  const [confirmEmail, {loading}] = useConfirmUserEmailMutation()
  const {getPath} = useRoutes()

  useEffect(() => {
    if (token)
      try {
        confirmEmail({
          variables: {token},
          refetchQueries: [{query: MeDocument}],
          awaitRefetchQueries: true,
        }).then((payload) => {
          if (payload.data?.confirmUserEmail?.code !== 200) {
            history.push(getPath(RouteEnum.EMAIL_TOKEN_EXPIRED))
          }
        })
      } catch (e) {
        console.error(e)
      }
  }, [token, confirmEmail, history, getPath])

  return (
    <>
      {loading ? (
        <PageLoader position={'center'} />
      ) : (
        <SimpleMessageContainer
          CustomImage={
            <Box
              sx={{
                background: theme.palette.neutral[50],
                borderRadius: '50%',
                height: 100,
                width: 100,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <CheckGraphicSVG style={{width: 50, height: 50}} />
            </Box>
          }
          actionArea={[
            <SubmitButton
              key={'submit-button'}
              onClick={() => history.push(getPath(RouteEnum.CREATE_ACCOUNT))}
            >
              {t('validate_email_setup_my_account')}
            </SubmitButton>,
          ]}
          message={
            <>
              <Typography variant={'h2'} gutterBottom>
                {t('validate_email_thank_you')}
              </Typography>
              <Typography variant='body2' textAlign={'center'}>
                {t(`Welcome to Progyny! Your email address is now verified.`)}
              </Typography>
              <br />
              <Typography variant='body2' textAlign={'center'}>
                {t('validate_email_let_s_setup_your_account')}
              </Typography>
            </>
          }
        />
      )}
    </>
  )
}

export default ValidateToken
